import React, { useEffect, useCallback, startTransition } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    Box,
    IconButton,
    CircularProgress,
    Button,
    Chip,
    TablePagination,
    TableSortLabel,
} from '@mui/material';
import { Edit, Delete, CheckCircle, Cancel, Email, Work, HourglassEmpty, Add } from '@mui/icons-material';
import CreateDeveloperDialog from './CreateDeveloperDialog';
import clientService from 'src/clientService';
import { useSnackbar } from 'src/view/components/SnackbarService';
import { AppSchemasUserUserResponse, UserRole, UserStatus } from 'src/api/Api';
import ConfirmationDialog from 'src/view/components/ConfirmationDialog';
import { useAppState } from 'src/state-management/state-provider';
import { useNavigate } from 'react-router-dom';


interface HeadCell {
    id: keyof AppSchemasUserUserResponse;
    label: string;
    numeric: boolean;
    sortable: boolean;
}

const headCells: HeadCell[] = [
    { id: 'email', numeric: false, label: 'Email', sortable: true },
    { id: 'first_name', numeric: false, label: 'Name', sortable: true }, // Merged first_name and last_name
    { id: 'role', numeric: false, label: 'Role', sortable: false },
    { id: 'status', numeric: false, label: 'Status', sortable: true },
    { id: 'is_email_verified', numeric: false, label: 'Email Verified', sortable: true },
];

const Users: React.FC = () => {
    const { state, dispatch } = useAppState();
    const { users: userState } = state.admin;
    const { data: users, totalUsers, loading, error, page, rowsPerPage, orderBy, order } = userState;
    const navigate = useNavigate();

    const [openDeleteDialog, setOpenDeleteDialog] = React.useState<boolean>(false);
    const [userToDelete, setUserToDelete] = React.useState<string | null>(null);
    const [openCreateDialog, setOpenCreateDialog] = React.useState<boolean>(false);

    const { showSnackbar, snackbarType } = useSnackbar();

    const fetchUsers = useCallback(async () => {
        dispatch({ type: 'ADMIN_SET_USERS_LOADING', payload: true });
        try {
            const response = await clientService.api.listUsersApiV1AdminUsersGet({
                page: page + 1, // API expects 1-indexed page
                per_page: rowsPerPage,
                sort_by: orderBy,
                sort_order: order,
                query: ''
            });
            dispatch({ type: 'ADMIN_SET_USERS', payload: { users: response.data.users || [], totalUsers: response.data.count || 0 } });
        } catch (err: any) {
            const errorMessage = err?.detail?.[0]?.msg || err?.message || 'Failed to fetch users.';
            dispatch({ type: 'ADMIN_SET_USERS_ERROR', payload: errorMessage });
            showSnackbar({ message: `Error: ${errorMessage}`, type: snackbarType.ERROR });
        }
    }, [page, rowsPerPage, orderBy, order]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const handleDeleteClick = (userId: string) => {
        setUserToDelete(userId);
        setOpenDeleteDialog(true);
    };

    const handleConfirmDelete = async () => {
        if (userToDelete) {
            try {
                await clientService.api.deleteUserApiV1AdminUsersUserIdDelete(userToDelete);
                showSnackbar({ message: 'User deleted successfully!', type: snackbarType.SUCCESS });
                dispatch({ type: 'ADMIN_DELETE_USER', payload: userToDelete });
            } catch (err: any) {
                const errorMessage = err?.detail?.[0]?.msg || err?.message || 'Failed to delete user.';
                showSnackbar({ message: `Error: ${errorMessage}`, type: snackbarType.ERROR });
            } finally {
                setOpenDeleteDialog(false);
                setUserToDelete(null);
            }
        }
    };

    const handleEditClick = (user: AppSchemasUserUserResponse) => { // Changed parameter to user object
        startTransition(() => {
            navigate(`/admin/user/${user.user_id}/update`);
        });
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        console.log('handleChangePage: newPage', newPage);
        dispatch({ type: 'ADMIN_SET_USERS_PAGINATION', payload: { page: newPage, rowsPerPage } });
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        console.log('handleChangeRowsPerPage: newRowsPerPage', parseInt(event.target.value, 10));
        dispatch({ type: 'ADMIN_SET_USERS_PAGINATION', payload: { page: 0, rowsPerPage: parseInt(event.target.value, 10) } });
    };

    const handleRequestSort = (
        event: React.MouseEvent<unknown>,
        property: keyof AppSchemasUserUserResponse,
    ) => {
        const isAsc = orderBy === property && order === 'asc';
        const newOrder = isAsc ? 'desc' : 'asc';
        console.log('handleRequestSort: property', property, 'newOrder', newOrder);
        startTransition(() => {
            dispatch({ type: 'ADMIN_SET_USERS_SORTING', payload: { orderBy: property, order: newOrder } });
        });
    };

    // Removed handleSearchChange and localSearchQuery state

    if (loading && users.length === 0) { // Show full screen loader only on initial load or when no data
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3, textAlign: 'center', color: 'error.main' }}>
                <Typography variant="h6">Error Loading Users</Typography>
                <Typography>{error}</Typography>
                <Button onClick={fetchUsers} variant="outlined" sx={{ mt: 2 }}>Retry</Button>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Removed search TextField */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5">Users</Typography>
                <Button 
                    variant="contained" 
                    startIcon={<Add />} 
                    onClick={() => setOpenCreateDialog(true)}
                >
                    Create Developer
                </Button>
            </Box>
            <TableContainer component={Paper} elevation={3}>
                <Table sx={{ minWidth: 650 }} aria-label="users table">
                    <TableHead>
                        <TableRow>
                            {headCells.map((headCell) => (
                                <TableCell
                                    key={headCell.id as string}
                                    align={headCell.numeric ? 'right' : 'left'}
                                    padding="normal"
                                    sortDirection={orderBy === headCell.id ? order : false}
                                >
                                    {headCell.sortable ? (
                                        <TableSortLabel
                                            active={orderBy === headCell.id}
                                            direction={orderBy === headCell.id ? order : 'asc'}
                                            onClick={(event) => handleRequestSort(event, headCell.id)}
                                        >
                                            {headCell.label}
                                        </TableSortLabel>
                                    ) : (
                                        headCell.label
                                    )}
                                </TableCell>
                            ))}
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading && users.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={headCells.length + 1} align="center">
                                    <CircularProgress size={24} />
                                </TableCell>
                            </TableRow>
                        ) : users.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={headCells.length + 1} align="center">
                                    No users found.
                                </TableCell>
                            </TableRow>
                        ) : (
                            users.map((user) => (
                                <TableRow key={`${user.user_id}-${user.status}-${user.status}-${user.role?.join(',')}`}>
                                    <TableCell>
                                        <Chip icon={<Email />} label={user.email} variant="outlined" size="small" />
                                    </TableCell>
                                    <TableCell>{`${user.first_name || ''} ${user.last_name || ''}`.trim() || 'N/A'}</TableCell>
                                    <TableCell>
                                        {user.role && user.role.length > 0 ? (
                                            user.role.map((r) => (
                                                <Chip key={r} icon={<Work />} label={r} variant="outlined" size="small" sx={{ mr: 0.5 }} />
                                            ))
                                        ) : (
                                            <Chip icon={<Work />} label={UserRole.User} variant="outlined" size="small" />
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            key={`${user.user_id}-${user.status}`} // Added key for re-rendering
                                            icon={<HourglassEmpty />}
                                            label={user.status || UserStatus.Active}
                                            variant="outlined"
                                            size="small"
                                            color={user.status === UserStatus.Active ? 'success' : user.status === UserStatus.Suspended ? 'warning' : 'default'}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {user.is_email_verified ? (
                                            <CheckCircle color="success" />
                                        ) : (
                                            <Cancel color="error" />
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton
                                            aria-label="edit"
                                            onClick={() => handleEditClick(user)}
                                            color="primary"
                                        >
                                            <Edit />
                                        </IconButton>
                                        <IconButton
                                            aria-label="delete"
                                            onClick={() => handleDeleteClick(user.user_id)}
                                            color="error"
                                        >
                                            <Delete />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={totalUsers}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />

            <ConfirmationDialog
                open={openDeleteDialog}
                title="Confirm Deletion"
                content="Are you sure you want to delete this user? This action is irreversible. Deleting this user will permanently remove all associated projects, sites, locations, deployments, and uploaded files. To revoke access while retaining user data, consider suspending the user instead."
                onConfirm={handleConfirmDelete}
                onCancel={() => setOpenDeleteDialog(false)}
                confirmButtonText="Delete"
                cancelButtonText="Cancel"
            />

            <CreateDeveloperDialog 
                open={openCreateDialog} 
                onClose={() => setOpenCreateDialog(false)} 
                onUserCreated={fetchUsers} 
            />


        </Box>
    );
};

export default Users;